/**
 * Server-side template detection.
 * Reads configuration from Cloudflare Worker–injected window.__TEMPLATE_CONFIG__
 * (shape: templateName, province, detectionMethod, etc.). Cookie fallback uses
 * Worker cookie names: user_country, template_type, user_language, user_province.
 */

/** ISO2 en mayúsculas o "UNKNOWN". Evita que `country === "AR"` falle si el Worker manda "ar". */
export function normalizeWorkerCountry(raw: unknown): string {
  if (raw === undefined || raw === null) return "UNKNOWN";
  const s = String(raw).trim();
  if (!s) return "UNKNOWN";
  const up = s.toUpperCase();
  if (up === "UNKNOWN") return "UNKNOWN";
  if (/^[A-Z]{2}$/.test(up)) return up;
  const two = up.slice(0, 2);
  if (/^[A-Z]{2}$/.test(two)) return two;
  return "UNKNOWN";
}

function normalizeWorkerLanguage(raw: unknown, countryCode: string): string {
  const s = String(raw ?? "").trim().replace(/_/g, "-");
  if (!s) return countryCode === "AR" ? "es-AR" : "en-US";
  return s;
}

export interface ServerTemplateConfig {
  /** Worker bypass: /admin SPA — no visitor geo cookies or CTA flags. */
  adminSpaBypass?: boolean;
  country: string;
  templateName: string;
  bioLayout: string | null;
  language: string;
  province: string | null;
  /** Método de detección de provincia AR en el Worker (p. ej. iso_region_code, default_fallback). */
  provinceDetectionMethod?: string | null;
  cfCountry: string;
  timezone: string;
  /**
   * Effective city after the Worker's alias + polygon resolution.
   * For Argentina, "Capital Federal" (raw CF) becomes "Buenos Aires" here.
   * For visitors with empty CF.city but matching polygon, this is the polygon-derived city.
   * Use this for display/CTA logic; use `cfCity` only for debugging.
   */
  city: string;
  /** Raw `request.cf.city` from Cloudflare. May differ from `city` after alias resolution. */
  cfCity?: string;
  detectionMethod: string;
  /**
   * Worker flag for "Best Friend Instagram" (Close Friends) CTA in AR provinces.
   * Kept as `whatsappBroadcast` to preserve Cloudflare Worker contract — the
   * frontend renames it to `bestfriendInstagram` on the way out (useTemplateSelection).
   */
  whatsappBroadcast?: boolean;
  /** Wishlist CTA (Worker / admin `cta_type=wishlist`; en AR según provincia o DB) */
  showWishlist?: boolean;
  /** Hide all CTA buttons for this province (AR_NO_CTA_PROVINCES in Worker) */
  hideCta?: boolean;
  /** CTA comunidad → /invitacion-premium (Worker / DB) */
  showCommunity?: boolean;
  /** CTA cumpleaños (Worker) */
  showBirthday?: boolean;
  /**
   * DualCTAVideoLanding CTA flag (video Mux + Stripe checkout) — exterior only.
   * Worker garantiza que AR nunca lo expone (`exposedShowDual` lo suprime en worker.js).
   */
  showDual?: boolean;
  /** Redirige BioTemplatePremiumSimple al menú VIP (cliente SPA) */
  redirectMenuVipReels?: boolean;
  /** True si la IP parece pertenecer a un VPN/datacenter/cloud (cf.asOrganization) */
  vpnDetected?: boolean;
  /** Nombre de la organización ASN detectada por Cloudflare */
  asOrganization?: string;
  /**
   * CTA checkout directo → auto-ruta a MercadoPago (AR) o Stripe (internacional).
   * Se activa cuando wishlist_geo_config.cta_type = "checkout" para este geo.
   */
  showCheckout?: boolean;
  /**
   * CTA Instagram Mejores Amigos (Close Friends) → hero button con gradiente IG.
   * Se activa cuando wishlist_geo_config.cta_type = "mejores_amigos" para este geo.
   */
  showBestFriends?: boolean;
  /** URL destino custom del CTA checkout (desde wishlist_geo_config.url vía Worker). Si no está, se usa la ruta por defecto AR / internacional. */
  ctaUrl?: string;
  /** Texto custom del botón CTA (desde wishlist_geo_config.label vía Worker). */
  ctaLabel?: string;
  /** Tipo CTA resuelto por el Worker (none, community, checkout, …) — debug / siteDebug. */
  heroCtaResolved?: string;
  /** Fuente de la cascada (edge_fallback, province_ar, metro_premium_override, …). */
  heroCtaSource?: string;
  /**
   * true cuando CountryTemplateAdmin tiene override explícito para este país
   * (Worker `countryTemplateAdminOverride`). Usado para no recargar toda la web
   * ante un cambio en country_template:DEFAULT.
   */
  countryTemplateOverride?: boolean;
  /**
   * Tier URLs para el CTA Mejores Amigos (IG Close Friends).
   * Cuando showBestFriends=true el hero rendea DOS botones (Precio Medio + Alto)
   * leyendo de acá. URLs vienen de `mejores_amigos_price_config` (globales por
   * brand). Si solo hay uno seteado, se rendea solo ese.
   */
  mejoresAmigosTiers?: {
    medium?: { url: string; label: string };
    high?: { url: string; label: string };
  };
  /**
   * IPLocate enrichment fields (added 2026-06).
   * Worker uses CF coordinates → bbox lookup against argentinaNeighborhoods.js.
   * IP→barrio is NOT 100% reliable; use for soft CTAs (events, meetups), not gating.
   */
  neighborhood?: string | null;
  /** City derived from neighborhood polygon hit — may differ from CF.city in some edge cases. */
  neighborhoodCity?: string | null;
  /** Geo data source: where the most-specific data came from. */
  geoSource?:
    | "cf_only"
    | "iplocate"
    | "cache"
    | "neighborhood_polygon"
    | "device_geolocation"
    | "manual_override";
  /** Privacy flags from IPLocate.io (VPN/proxy/Tor/hosting/abuse). */
  privacyFlags?: {
    is_vpn?: boolean;
    is_proxy?: boolean;
    is_tor?: boolean;
    is_relay?: boolean;
    is_hosting?: boolean;
    is_abuser?: boolean;
    is_attacker?: boolean;
    is_threat?: boolean;
    is_anonymous?: boolean;
    is_bogon?: boolean;
  };
  /** Hosting/datacenter signal (commonly bots / scrapers). */
  isHosting?: boolean;
  /** ASN details from IPLocate.io. */
  asnDetails?: {
    asn?: string | number;
    name?: string;
    domain?: string;
  };
  /**
   * Worker decide BaseTemplate vs BaseTemplateInternational (2026-06-14).
   * El cliente NO re-deriva esta decisión; antes mezclaba usesSpanishDefaultForSite()
   * + country + showWishlist en runtime y podía discrepar con el Worker cuando una
   * cookie stale forzaba bucket AR.
   */
  useInternationalLayout?: boolean;
  /**
   * Admin heroCTA override resuelto por el Worker (solo presente cuando el template
   * es BaseTemplate / BaseTemplateBlack — el Worker lo gatea ahí). Cuando está set,
   * BaseTemplate renderiza este botón en lugar del hero CTA geo derivado.
   * Fuente: /admin/site-settings → KV → Worker, con membership URL resolved por país.
   */
  heroCta?: {
    enabled: true;
    label: string;
    url: string;
    emoji?: string;
    type: "membership" | "link";
  };
  /**
   * siteSettings completo pre-resuelto por el Worker.
   * Incluye heroCta (settings coherente con geo) + brandAge.
   * Usado como estado inicial (sin fetch). Worker reescribe el HTML de Railway.
   * Polling/fetch para actualizaciones.
   */
  siteSettings?: {
    headerUrl?: string;
    title?: string;
    description?: string;
    logoUrl?: string;
    sobreMi?: string;
    showSobreMi?: boolean;
    galleryImageUrls?: string[];
    bannerImageUrl?: string;
    footerTagline?: string;
    alternativeGalleryEnabled?: boolean;
    alternativeGalleryUrls?: string[];
    showGallerySection?: boolean;
    showTallySection?: boolean;
    brandAge?: string;
    // Hero CTA override (site-settings). El Worker puede inyectar shape camelCase
    // (contrato /api/site-settings) o snake_case (KV vieja solo heroCta_*);
    // normalizeSiteSettingsFromWorker acepta ambas y emite solo camelCase.
    heroCtaEnabled?: boolean;
    heroCtaLabel?: string;
    heroCtaType?: "link" | "membership";
    heroCtaUrl?: string;
    heroCtaMpUrl?: string;
    heroCtaStripeUrl?: string;
    heroCtaEmoji?: string;
    heroCta_enabled?: boolean;
    heroCta_label?: string;
    heroCta_type?: "link" | "membership";
    heroCta_url?: string;
    heroCta_mp_url?: string;
    heroCta_stripe_url?: string;
    heroCta_emoji?: string;
  };
  /**
   * Config DualCTAVideoLanding por país (coordinada desde /admin/country-template
   * → country_template_config.dual_cta_config → KV → Worker). Solo presente cuando
   * el template del país es DualCTAVideoLanding. El landing la usa como override
   * autoritativo: mux que reproduce, label del CTA y Stripe Price ID a cobrar.
   */
  /**
   * Admin Geo Simulator (Worker cookie/gst HMAC). Present when edge applied
   * authorized geo override — toolbar banner + analytics filter.
   */
  adminGeoSim?: {
    country?: string;
    state?: string;
    province?: string;
    city?: string;
    vpn?: boolean;
    hosting?: boolean;
    icloudRelay?: boolean;
    simulateVpn?: string;
    simulateIp?: string;
    privacyClean?: boolean;
    expiresAt?: string;
  };
  /**
   * Mux resuelto por geo AR fino (cta-por-región: barrio→ciudad→provincia).
   * DualCTA lo prioriza sobre dualCta.muxPlaybackId de país cuando está seteado.
   */
  dualCtaGeo?: {
    muxPlaybackId?: string;
    source?: "neighborhood_ar" | "city_ar" | "province_ar" | string;
    geoCode?: string;
  };
  dualCta?: {
    muxPlaybackId?: string;
    /**
     * ID de un "Dual CTA Video Template" (vip_video_assets) asignado a este país
     * desde /admin/country-template. El landing lo resuelve vía
     * /api/dual-cta-template (mux + precio + descuento + stripe). Los campos
     * directos del país (muxPlaybackId/stripePriceId/…) ganan sobre el template.
     */
    vipVideoAssetId?: number;
    vipLabel?: string;
    freeLabel?: string;
    stripePriceId?: string;
    /** Oculta el CTA VIP en este país. */
    hideVipCta?: boolean;
    /** Destino del CTA VIP (path interno o https) — pisa el checkout Stripe inline. */
    vipHref?: string;
    /** CTA gratis por país: true/false fuerza; ausente hereda el toggle global. */
    freeCtaEnabled?: boolean;
    /** URL de Telegram del CTA gratis en este país (pisa la global). */
    freeTelegramUrl?: string;
    /** Segundo del video usado como poster/primer frame (default 0). */
    posterTime?: number;
    /** Texto del estado "cargando" del CTA en este país. */
    loadingLabel?: string;
    /** Mux playback id solo para el poster (frame de otro video). */
    posterPlaybackId?: string;
    /** URL de imagen (https) como poster custom; pisa mux/frame. */
    posterImageUrl?: string;
    /** true = CTA gratis arriba del VIP en este país. */
    freeCtaFirst?: boolean;
    /** true = video en loop en este país. */
    videoLoop?: boolean;
    /** Texto cuando el video no carga (pisa el i18n). */
    videoUnavailableText?: string;
    /** Hint "podés unirte ahora" mientras carga el video. */
    loadingJoinHint?: string;
    /**
     * % descuento (1-100) solo para el primer mes (cupón repeating).
     * Aplicable a templates internacionales con Mux (DualCTAVideoLanding).
     * Si presente >0, el init del checkout VIP usará discount + duration 1 mes.
     */
    firstMonthDiscountPercent?: number;
    /** Precio full USD/mes para copy en landing (opcional). */
    fullPriceUsd?: number;
  };
}

/**
 * Get template configuration from server-injected window object
 * The Cloudflare Worker injects this before the page loads
 */
/** Worker inyectó hero sin CTA (hideCta + ningún show* activo). */
export function isWorkerHeroCtaResolvedEmpty(
  cfg: ServerTemplateConfig | null | undefined,
): boolean {
  if (!cfg) return false;
  if (cfg.heroCta?.enabled) return false;
  return (
    !!cfg.hideCta &&
    !cfg.showCheckout &&
    !cfg.showCommunity &&
    !cfg.showBirthday &&
    !cfg.showWishlist &&
    !cfg.showBestFriends
  );
}

/** HTML del Worker ya trae un CTA geo activo (community, checkout, etc.). */
export function workerHtmlHasPositiveHeroCta(
  cfg: ServerTemplateConfig | null | undefined,
): boolean {
  if (!cfg) return false;
  if (cfg.heroCta?.enabled) return true;
  return (
    !cfg.hideCta &&
    !!(
      cfg.showCheckout ||
      cfg.showCommunity ||
      cfg.showBirthday ||
      cfg.showWishlist ||
      cfg.showBestFriends
    )
  );
}

/**
 * ¿Hace falta un fetch defensivo a /api/cta-flags en el cliente?
 * No cuando el Worker ya resolvió CTA vacío para esta geo (p. ej. Monserrat →
 * province_ar_none, hideCta). Sí con CTA activo (refresh post-admin) o HTML sin
 * heroCtaResolved (posible stale).
 */
export function workerCtaNeedsDefensiveFetch(
  cfg: ServerTemplateConfig | null | undefined,
): boolean {
  if (!cfg) return true;
  if (cfg.heroCta?.enabled) return false;
  if (workerHtmlHasPositiveHeroCta(cfg)) return true;
  if (
    isWorkerHeroCtaResolvedEmpty(cfg) &&
    (cfg.heroCtaResolved || "").trim().toLowerCase() === "none"
  ) {
    return false;
  }
  return true;
}

// Memoizado: el Worker inyecta window.__TEMPLATE_CONFIG__ una sola vez antes de
// servir el HTML y NO lo muta durante la sesión. Sin cache, cada render genera un
// objeto config nuevo; los hooks que lo usan como dependencia (p. ej.
// useFreshCtaFlags) re-disparan su effect en bucle → flood de /api/cta-flags
// abortados → net::ERR_INSUFFICIENT_RESOURCES, que mata de hambre a
// /api/site-settings + /api/portfolio (galería vacía). Cachear por identidad de
// la fuente (objeto inyectado o string de cookies) da una referencia estable y
// rompe el bucle para todos los consumidores. Una recarga real (p. ej. ?country=XX)
// reinicia el módulo, así que el override de dev sigue funcionando.
let cachedConfigSource: object | string | null = null;
let cachedConfig: ServerTemplateConfig | null = null;

function normalizeSiteSettingsFromWorker(
  raw: unknown,
): ServerTemplateConfig["siteSettings"] | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const src = raw as Record<string, unknown>;
  const galleryImageUrls = Array.isArray(src.galleryImageUrls)
    ? (src.galleryImageUrls as unknown[]).filter(
        (u): u is string => typeof u === "string" && u.trim().length > 0,
      )
    : undefined;
  const alternativeGalleryUrls = Array.isArray(src.alternativeGalleryUrls)
    ? (src.alternativeGalleryUrls as unknown[]).filter(
        (u): u is string => typeof u === "string" && u.trim().length > 0,
      )
    : undefined;
  const out: NonNullable<ServerTemplateConfig["siteSettings"]> = {
    headerUrl:
      typeof src.headerUrl === "string" && src.headerUrl.trim()
        ? src.headerUrl.trim()
        : undefined,
    title:
      typeof src.title === "string" && src.title.trim()
        ? src.title.trim()
        : undefined,
    description:
      typeof src.description === "string" && src.description.trim()
        ? src.description.trim()
        : undefined,
    logoUrl:
      typeof src.logoUrl === "string" && src.logoUrl.trim()
        ? src.logoUrl.trim()
        : undefined,
    sobreMi:
      typeof src.sobreMi === "string" && src.sobreMi.trim()
        ? src.sobreMi.trim()
        : undefined,
    showSobreMi:
      typeof src.showSobreMi === "boolean" ? src.showSobreMi : undefined,
    galleryImageUrls:
      galleryImageUrls && galleryImageUrls.length > 0 ? galleryImageUrls : undefined,
    bannerImageUrl:
      typeof src.bannerImageUrl === "string" && src.bannerImageUrl.trim()
        ? src.bannerImageUrl.trim()
        : undefined,
    footerTagline:
      typeof src.footerTagline === "string" && src.footerTagline.trim()
        ? src.footerTagline.trim()
        : undefined,
    alternativeGalleryEnabled:
      typeof src.alternativeGalleryEnabled === "boolean"
        ? src.alternativeGalleryEnabled
        : undefined,
    alternativeGalleryUrls:
      alternativeGalleryUrls && alternativeGalleryUrls.length > 0
        ? alternativeGalleryUrls
        : undefined,
    showGallerySection:
      typeof src.showGallerySection === "boolean"
        ? src.showGallerySection
        : undefined,
    showTallySection:
      typeof src.showTallySection === "boolean" ? src.showTallySection : undefined,
    brandAge:
      typeof src.brandAge === "string" && src.brandAge.trim()
        ? src.brandAge.trim()
        : undefined,
    // Hero CTA from site-settings — unified source with worker injection for coherence
    // across build-time HTML, worker rewrite, and /api fetch.
    heroCtaEnabled:
      typeof src.heroCtaEnabled === "boolean" ? src.heroCtaEnabled :
      typeof src.heroCta_enabled === "boolean" ? src.heroCta_enabled : undefined,
    heroCtaLabel:
      typeof src.heroCtaLabel === "string" && src.heroCtaLabel.trim() ? src.heroCtaLabel.trim() :
      typeof src.heroCta_label === "string" && src.heroCta_label.trim() ? src.heroCta_label.trim() : undefined,
    // undefined si el Worker no mandó el campo: un default "link" acá haría que
    // el guard some(v => v !== undefined) de abajo nunca devuelva undefined.
    heroCtaType:
      (src.heroCtaType === "membership" || src.heroCta_type === "membership") ? "membership" :
      (src.heroCtaType === "link" || src.heroCta_type === "link") ? "link" : undefined,
    heroCtaUrl:
      typeof src.heroCtaUrl === "string" && src.heroCtaUrl.trim() ? src.heroCtaUrl.trim() :
      typeof src.heroCta_url === "string" && src.heroCta_url.trim() ? src.heroCta_url.trim() : undefined,
    heroCtaMpUrl:
      typeof src.heroCtaMpUrl === "string" && src.heroCtaMpUrl.trim() ? src.heroCtaMpUrl.trim() :
      typeof src.heroCta_mp_url === "string" && src.heroCta_mp_url.trim() ? src.heroCta_mp_url.trim() : undefined,
    heroCtaStripeUrl:
      typeof src.heroCtaStripeUrl === "string" && src.heroCtaStripeUrl.trim() ? src.heroCtaStripeUrl.trim() :
      typeof src.heroCta_stripe_url === "string" && src.heroCta_stripe_url.trim() ? src.heroCta_stripe_url.trim() : undefined,
    heroCtaEmoji:
      typeof src.heroCtaEmoji === "string" && src.heroCtaEmoji.trim() ? src.heroCtaEmoji.trim() :
      typeof src.heroCta_emoji === "string" && src.heroCta_emoji.trim() ? src.heroCta_emoji.trim() : undefined,
  };
  return Object.values(out).some((v) => v !== undefined) ? out : undefined;
}

/**
 * Normaliza un dual_cta_config crudo (Worker inject o endpoint público) al tipo
 * ServerTemplateConfig["dualCta"]. Exportado para el modo preview por país de
 * DualCTAVideoLanding (?checkout_country=XX fetchea /api/country-template-config).
 */
export function normalizeDualCtaFromWorker(
  raw: unknown,
): ServerTemplateConfig["dualCta"] | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const src = raw as Record<string, unknown>;
  const str = (v: unknown): string | undefined =>
    typeof v === "string" && v.trim() ? v.trim() : undefined;
  const bool = (v: unknown): boolean | undefined =>
    typeof v === "boolean" ? v : undefined;
  const num = (v: unknown): number | undefined =>
    typeof v === "number" && Number.isFinite(v) && v > 0 ? v : undefined;
  // posterTime allows 0 (first frame); other numeric dualCta fields stay > 0.
  const posterSec = (v: unknown): number | undefined =>
    typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : undefined;
  const intId = (v: unknown): number | undefined => {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isInteger(n) && n > 0 ? n : undefined;
  };
  const out: NonNullable<ServerTemplateConfig["dualCta"]> = {
    muxPlaybackId: str(src.muxPlaybackId),
    vipVideoAssetId: intId(src.vipVideoAssetId),
    vipLabel: str(src.vipLabel),
    freeLabel: str(src.freeLabel),
    stripePriceId: str(src.stripePriceId),
    hideVipCta: bool(src.hideVipCta),
    vipHref: str(src.vipHref),
    freeCtaEnabled: bool(src.freeCtaEnabled),
    freeTelegramUrl: str(src.freeTelegramUrl),
    posterTime: posterSec(src.posterTime),
    loadingLabel: str(src.loadingLabel),
    posterPlaybackId: str(src.posterPlaybackId),
    posterImageUrl: str(src.posterImageUrl),
    freeCtaFirst: bool(src.freeCtaFirst),
    videoLoop: bool(src.videoLoop),
    videoUnavailableText: str(src.videoUnavailableText),
    loadingJoinHint: str(src.loadingJoinHint),
    firstMonthDiscountPercent: num(src.firstMonthDiscountPercent),
    fullPriceUsd: num(src.fullPriceUsd),
  };
  return Object.values(out).some((v) => v !== undefined) ? out : undefined;
}

export function getServerTemplateConfig(): ServerTemplateConfig | null {
  if (typeof window === "undefined") return null;

  const injected = (
    window as unknown as { __TEMPLATE_CONFIG__?: unknown }
  ).__TEMPLATE_CONFIG__;
  const source: object | string =
    injected && typeof injected === "object"
      ? (injected as object)
      : document.cookie;
  if (cachedConfig !== null && cachedConfigSource === source) {
    return cachedConfig;
  }

  const result = computeServerTemplateConfig();
  if (result !== null) {
    cachedConfigSource = source;
    cachedConfig = result;
  }
  return result;
}

function computeServerTemplateConfig(): ServerTemplateConfig | null {
  if (typeof window === "undefined") return null;

  // Primary source: injected by Cloudflare Worker
  const serverConfig = (
    window as unknown as {
      __TEMPLATE_CONFIG__?: Partial<ServerTemplateConfig> & Record<string, unknown>;
    }
  ).__TEMPLATE_CONFIG__;

  if (serverConfig) {
    if (serverConfig.adminSpaBypass === true) {
      return {
        adminSpaBypass: true,
        country: "UNKNOWN",
        templateName: "BaseTemplate",
        bioLayout: null,
        language: "es",
        province: null,
        cfCountry: "UNKNOWN",
        timezone: "",
        city: "",
        detectionMethod: "admin_spa_bypass",
      };
    }
    const country = normalizeWorkerCountry(serverConfig.country);
    // Worker = autoridad. Antes acá había una "defensa AR" que forzaba BaseTemplate
    // si llegaba DualCTAVideoLanding — duplicaba la lógica que el Worker ya tiene en
    // worker.js (rama AR → BaseTemplate). Removida 2026-06-14: si Worker dice X,
    // renderizamos X. Sin segunda decisión cliente-lado.
    const templateName = serverConfig.templateName || "BaseTemplate";
    return {
      country,
      templateName,
      bioLayout: serverConfig.bioLayout || null,
      language: normalizeWorkerLanguage(serverConfig.language, country),
      province: serverConfig.province || null,
      provinceDetectionMethod:
        serverConfig.provinceDetectionMethod != null
          ? String(serverConfig.provinceDetectionMethod)
          : null,
      cfCountry: normalizeWorkerCountry(
        serverConfig.cfCountry ?? serverConfig.country,
      ),
      timezone: serverConfig.timezone || "",
      city: serverConfig.city || "",
      cfCity: typeof serverConfig.cfCity === "string" ? serverConfig.cfCity : undefined,
      detectionMethod: serverConfig.detectionMethod || "cloudflare_geo",
      whatsappBroadcast: !!serverConfig.whatsappBroadcast,
      showWishlist: !!serverConfig.showWishlist,
      hideCta: !!serverConfig.hideCta,
      showCommunity: !!serverConfig.showCommunity,
      showBirthday: !!serverConfig.showBirthday,
      // Worker garantiza que AR nunca lleva showDual (lo suprime en exposedShowDual).
      // No re-validamos acá: lo que diga el Worker, manda.
      showDual: !!serverConfig.showDual,
      useInternationalLayout: serverConfig.useInternationalLayout === true,
      heroCta: (() => {
        const raw = serverConfig.heroCta as ServerTemplateConfig["heroCta"] | undefined;
        if (!raw || raw.enabled !== true) return undefined;
        const label = typeof raw.label === "string" ? raw.label.trim() : "";
        const url = typeof raw.url === "string" ? raw.url.trim() : "";
        if (!label || !url) return undefined;
        const type = raw.type === "membership" ? "membership" : "link";
        const emoji = typeof raw.emoji === "string" && raw.emoji.trim() ? raw.emoji.trim() : undefined;
        return { enabled: true, label, url, type, emoji };
      })(),
      redirectMenuVipReels:
        serverConfig.redirectMenuVipReels === true ? true : undefined,
      vpnDetected: serverConfig.vpnDetected === true,
      asOrganization: typeof serverConfig.asOrganization === "string" ? serverConfig.asOrganization : undefined,
      showCheckout: !!serverConfig.showCheckout,
      showBestFriends: !!serverConfig.showBestFriends,
      ctaUrl: typeof serverConfig.ctaUrl === "string" && serverConfig.ctaUrl.trim() ? serverConfig.ctaUrl.trim() : undefined,
      ctaLabel: typeof serverConfig.ctaLabel === "string" && serverConfig.ctaLabel.trim() ? serverConfig.ctaLabel.trim() : undefined,
      heroCtaResolved:
        typeof serverConfig.heroCtaResolved === "string" && serverConfig.heroCtaResolved.trim()
          ? serverConfig.heroCtaResolved.trim()
          : undefined,
      heroCtaSource:
        typeof serverConfig.heroCtaSource === "string" && serverConfig.heroCtaSource.trim()
          ? serverConfig.heroCtaSource.trim()
          : undefined,
      countryTemplateOverride:
        serverConfig.countryTemplateOverride === true ? true : undefined,
      mejoresAmigosTiers: (() => {
        const raw = serverConfig.mejoresAmigosTiers as
          | { medium?: { url?: unknown; label?: unknown }; high?: { url?: unknown; label?: unknown } }
          | undefined;
        if (!raw || typeof raw !== "object") return undefined;
        const pickTier = (t: { url?: unknown; label?: unknown } | undefined) => {
          if (!t) return undefined;
          const url = typeof t.url === "string" && t.url.trim() ? t.url.trim() : null;
          const label = typeof t.label === "string" && t.label.trim() ? t.label.trim() : null;
          if (!url) return undefined;
          return { url, label: label ?? "" };
        };
        const medium = pickTier(raw.medium);
        const high = pickTier(raw.high);
        if (!medium && !high) return undefined;
        return { ...(medium ? { medium } : {}), ...(high ? { high } : {}) };
      })(),
      neighborhood:
        typeof serverConfig.neighborhood === "string" && serverConfig.neighborhood.trim()
          ? serverConfig.neighborhood.trim()
          : null,
      neighborhoodCity:
        typeof serverConfig.neighborhoodCity === "string" && serverConfig.neighborhoodCity.trim()
          ? serverConfig.neighborhoodCity.trim()
          : null,
      geoSource:
        typeof serverConfig.geoSource === "string"
          ? (serverConfig.geoSource as ServerTemplateConfig["geoSource"])
          : undefined,
      privacyFlags:
        serverConfig.privacyFlags && typeof serverConfig.privacyFlags === "object"
          ? (serverConfig.privacyFlags as ServerTemplateConfig["privacyFlags"])
          : undefined,
      isHosting: serverConfig.isHosting === true ? true : undefined,
      asnDetails:
        serverConfig.asnDetails && typeof serverConfig.asnDetails === "object"
          ? (serverConfig.asnDetails as ServerTemplateConfig["asnDetails"])
          : undefined,
      siteSettings: normalizeSiteSettingsFromWorker(serverConfig.siteSettings),
      dualCta: normalizeDualCtaFromWorker(serverConfig.dualCta),
      dualCtaGeo: (() => {
        const raw = serverConfig.dualCtaGeo as
          | { muxPlaybackId?: unknown; source?: unknown; geoCode?: unknown }
          | undefined;
        if (!raw || typeof raw !== "object") return undefined;
        const mux =
          typeof raw.muxPlaybackId === "string" && raw.muxPlaybackId.trim()
            ? raw.muxPlaybackId.trim()
            : "";
        if (!mux) return undefined;
        return {
          muxPlaybackId: mux,
          source:
            typeof raw.source === "string" && raw.source.trim()
              ? raw.source.trim()
              : undefined,
          geoCode:
            typeof raw.geoCode === "string" && raw.geoCode.trim()
              ? raw.geoCode.trim()
              : undefined,
        };
      })(),
      adminGeoSim: (() => {
        const raw = serverConfig.adminGeoSim;
        if (!raw || typeof raw !== "object") return undefined;
        const o = raw as Record<string, unknown>;
        return {
          country:
            typeof o.country === "string" ? o.country.toUpperCase() : undefined,
          state: typeof o.state === "string" ? o.state : undefined,
          province: typeof o.province === "string" ? o.province : undefined,
          city: typeof o.city === "string" ? o.city : undefined,
          vpn: o.vpn === true,
          hosting: o.hosting === true,
          icloudRelay: o.icloudRelay === true,
          simulateVpn:
            typeof o.simulateVpn === "string" ? o.simulateVpn : undefined,
          simulateIp:
            typeof o.simulateIp === "string" ? o.simulateIp : undefined,
          privacyClean: o.privacyClean === true,
          expiresAt: typeof o.expiresAt === "string" ? o.expiresAt : undefined,
        };
      })(),
    };
  }

  // Fallback: try to read from cookies (set by Worker)
  const cookies = document.cookie.split(";").reduce(
    (acc, cookie) => {
      const [key, value] = cookie.trim().split("=");
      acc[key] = value;
      return acc;
    },
    {} as Record<string, string>,
  );

  if (cookies.user_country) {
    let countryNorm = "";
    try {
      countryNorm = decodeURIComponent(cookies.user_country)
        .trim()
        .toUpperCase()
        .slice(0, 2);
    } catch {
      countryNorm = cookies.user_country.trim().toUpperCase().slice(0, 2);
    }
    // WhatsApp broadcast: only for AR with qualifying provinces
    const arProvinces = ["CABA", "Buenos Aires", "Santa Fe", "Entre Ríos", "Río Negro", "San Luis", "La Pampa"];
    const province = cookies.user_province || null;
    const whatsappBroadcast =
      countryNorm === "AR" && province && arProvinces.includes(province);

    const rawLang = cookies.user_language?.replace(/_/g, "-") || "";
    const langFromCookie =
      countryNorm === "AR"
        ? rawLang.toLowerCase().startsWith("es")
          ? rawLang
          : "es-AR"
        : rawLang || "en-US";

    return {
      country: countryNorm || normalizeWorkerCountry(cookies.user_country),
      templateName: cookies.template_type || "BaseTemplate",
      bioLayout: null,
      language: normalizeWorkerLanguage(
        langFromCookie,
        countryNorm || "UNKNOWN",
      ),
      province,
      provinceDetectionMethod: null,
      cfCountry: normalizeWorkerCountry(cookies.user_country),
      timezone: "",
      city: "",
      detectionMethod: "cookies_fallback",
      whatsappBroadcast,
      showWishlist: false, // cookies don't carry showWishlist; Worker re-evaluates on each request
      hideCta: false,
      showCommunity: false,
      showBirthday: false,
      redirectMenuVipReels: undefined,
      showCheckout: false,
      showBestFriends: false,
      ctaUrl: undefined,
      ctaLabel: undefined,
    };
  }

  return null;
}

/** Decode Worker geo strings (e.g. URL-encoded city names) for analytics display. */
function decodeGeoSegment(raw: string | null | undefined): string | undefined {
  if (raw == null || !String(raw).trim()) return undefined;
  const s = String(raw).trim();
  try {
    return decodeURIComponent(s.replace(/\+/g, " "));
  } catch {
    return s;
  }
}

/** Client-side overlay after async /api/geolocate?enrich=1 (Worker IPLocate is waitUntil on cache miss). */
let clientGeoEnrichmentOverlay: Record<string, string | undefined> | null = null;

export function setClientGeoEnrichmentOverlay(
  props: Record<string, string | undefined>,
): void {
  clientGeoEnrichmentOverlay = props;
}

export function clearClientGeoEnrichmentOverlay(): void {
  clientGeoEnrichmentOverlay = null;
}

/**
 * Worker-derived geo fields for PostHog / GA (complements IP-based $geoip_* on the person).
 * Use on high-value CTAs so alerts match country · province · city without encoded %20 artifacts.
 */
export function getWorkerGeoAnalyticsProps(): Record<string, string | undefined> {
  const workerProps = buildWorkerGeoProps();
  if (clientGeoEnrichmentOverlay) {
    // VERDAD ÚNICA DE LOCATION: el Worker manda. El enrichment del cliente
    // (/api/geolocate) solo COMPLETA campos que el Worker no resolvió
    // (VPN/ASN/hosting cuando IPLocate no corrió en el edge) — nunca pisa
    // country/province/city ya resueltos. Antes el overlay REEMPLAZABA todo
    // y los eventos salían con la provincia del fallback (p. ej. "BA" de
    // ipinfo) en vez de la del Worker ("CABA").
    const merged: Record<string, string | undefined> = { ...clientGeoEnrichmentOverlay };
    for (const [k, v] of Object.entries(workerProps)) {
      if (v !== undefined && v !== "") merged[k] = v;
    }
    return merged;
  }
  return workerProps;
}

function buildWorkerGeoProps(): Record<string, string | undefined> {
  const cfg = getServerTemplateConfig();
  if (!cfg) return {};
  const city = decodeGeoSegment(cfg.city);
  const province = cfg.province?.trim() || undefined;
  const country =
    cfg.country && cfg.country !== "UNKNOWN" ? cfg.country : undefined;
  const cf =
    cfg.cfCountry && cfg.cfCountry !== "UNKNOWN" ? cfg.cfCountry : undefined;
  const parts = [country, province, city].filter(
    (x): x is string => typeof x === "string" && x.length > 0,
  );
  const neighborhood = cfg.neighborhood?.trim() || undefined;
  const sim = cfg.adminGeoSim;
  const adminGeoSimActive = !!sim;
  return {
    worker_country: country,
    worker_cf_country: cf,
    worker_province: province,
    worker_city: city,
    worker_neighborhood: neighborhood,
    worker_geo_source: cfg.geoSource,
    worker_vpn_detected: cfg.vpnDetected ? "true" : undefined,
    worker_is_hosting: cfg.isHosting ? "true" : undefined,
    worker_asn_organization: cfg.asnDetails?.name || cfg.asOrganization,
    worker_timezone: cfg.timezone || undefined,
    worker_detection_method: cfg.detectionMethod || undefined,
    geo_summary: parts.length ? parts.join(" · ") : undefined,
    // Filter dashboards: admin_geo_simulation = true
    admin_geo_simulation: adminGeoSimActive ? "true" : undefined,
    simulated_country: sim?.country,
    simulated_state: sim?.state,
    simulated_province: sim?.province,
    simulated_city: sim?.city,
    simulated_vpn_mode:
      sim?.simulateVpn ||
      (sim?.vpn ? "anonymous" : sim?.hosting ? "hosting" : undefined),
  };
}
